import type { SessionLifecyclePhase, SessionLifecycleTiming } from '@core/entities/SessionLifecycle';
import type { SessionLifecycleNavigation } from '@core/ports/SessionLifecycleNavigation';
import type { SessionLifecycleObserver } from '@core/ports/SessionLifecycleObserver';
import { ExtendSessionUseCase } from '@core/usecases/ExtendSessionUseCase';
import { GetSessionLifecycleUseCase } from '@core/usecases/GetSessionLifecycleUseCase';
import { LogOutSessionUseCase } from '@core/usecases/LogOutSessionUseCase';

const sessionExpiryMessage = 'Your session has expired due to inactivity. Start again from the NHS App to continue.';
const manualLogoutMessage = 'You have been logged out. Log in again from the NHS App to continue.';
const sessionUnavailableMessage = 'Your session cannot be confirmed right now. Start again from the NHS App to continue.';
const sessionWarningTitle = 'Session ending soon';
const sessionWarningMessage = 'You have been inactive. Your session will end in 1 minute unless you continue.';
const logoutFailedMessage = 'We could not confirm you were logged out. Start again from the NHS App to continue safely.';
const activityRefreshMinimumIntervalMs = 1000;
const terminalPhases: ReadonlySet<SessionLifecyclePhase> = new Set(['expired', 'session-required', 'blocked']);

export interface SessionLifecycleSnapshot {
  phase: SessionLifecyclePhase;
  title?: string;
  message?: string;
  secondsRemaining?: number;
}

type SessionLifecycleListener = (snapshot: SessionLifecycleSnapshot) => void;

export class SessionLifecycleController implements SessionLifecycleObserver {
  private readonly listeners = new Set<SessionLifecycleListener>();

  private snapshot: SessionLifecycleSnapshot = { phase: 'idle' };

  private warningTimerId?: ReturnType<typeof globalThis.setTimeout>;

  private expiryTimerId?: ReturnType<typeof globalThis.setTimeout>;

  private countdownTimerId?: ReturnType<typeof globalThis.setInterval>;

  private refreshTimerId?: ReturnType<typeof globalThis.setTimeout>;

  private refreshInFlight = false;

  private refreshQueued = false;

  private lastRefreshStartedAtMs = 0;

  private sessionLifetimeMs?: number;

  private warningWindowMs?: number;

  private hasInitialized = false;

  public constructor(
    private readonly getSessionLifecycleUseCase: GetSessionLifecycleUseCase,
    private readonly extendSessionUseCase: ExtendSessionUseCase,
    private readonly logOutSessionUseCase: LogOutSessionUseCase,
    private readonly navigation: SessionLifecycleNavigation
  ) {}

  public getSnapshot(): SessionLifecycleSnapshot {
    return this.snapshot;
  }

  public subscribe(listener: SessionLifecycleListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public async initialize(): Promise<void> {
    if (this.hasInitialized) {
      return;
    }

    try {
      const result = await this.getSessionLifecycleUseCase.execute();
      if (result.kind === 'available') {
        this.hasInitialized = this.applyStatus(result.status.serverTimeUtc, {
          expiresAtUtc: result.status.expiresAtUtc,
          warningAtUtc: result.status.warningAtUtc
        });
        return;
      }

      if (result.kind === 'session-required') {
        this.hasInitialized = true;
        this.markSessionRequired(result.message);
        return;
      }

      this.markBlocked(result.message ?? sessionUnavailableMessage);
    } catch {
      this.markBlocked(sessionUnavailableMessage);
    }
  }

  public updateFromTiming(timing: SessionLifecycleTiming): void {
    if (terminalPhases.has(this.snapshot.phase)) {
      return;
    }

    this.applyTiming(timing, Date.now());
  }

  public recordActivity(): void {
    if (!this.hasInitialized || this.snapshot.phase !== 'active') {
      return;
    }

    if (this.sessionLifetimeMs === undefined || this.warningWindowMs === undefined) {
      return;
    }

    this.applyRelativeTiming(Date.now());
    this.scheduleActivityRefresh();
  }

  public async staySignedIn(): Promise<void> {
    await this.refreshSessionFromBff();
  }

  public async logOut(): Promise<void> {
    this.clearRefreshQueue();

    try {
      await this.logOutSessionUseCase.execute();
    } catch {
      this.markBlocked(logoutFailedMessage);
      return;
    }

    this.transitionToExpired(manualLogoutMessage);
  }

  public markSessionRequired(message: string): void {
    this.clearTimersAndRefreshQueue();
    this.updateSnapshot({
      phase: 'session-required',
      title: 'Session expired',
      message
    });
    this.navigateToExpiredRoute();
  }

  public markBlocked(message: string): void {
    this.clearTimersAndRefreshQueue();
    this.updateSnapshot({
      phase: 'blocked',
      title: 'Session unavailable',
      message
    });
    this.navigateToExpiredRoute();
  }

  private applyStatus(serverTimeUtc: string, timing: SessionLifecycleTiming): boolean {
    const serverTime = new Date(serverTimeUtc);
    const expiresAt = new Date(timing.expiresAtUtc);
    const warningAt = new Date(timing.warningAtUtc);
    if (!this.tryRememberLifecycleDurations(expiresAt, warningAt, serverTime.getTime())) {
      this.markBlocked(sessionUnavailableMessage);
      return false;
    }

    return this.applyTiming(timing, Date.now());
  }

  private applyRelativeTiming(referenceTimeMs: number): boolean {
    if (this.sessionLifetimeMs === undefined || this.warningWindowMs === undefined) {
      return false;
    }

    return this.applyTiming({
      expiresAtUtc: new Date(referenceTimeMs + this.sessionLifetimeMs).toISOString(),
      warningAtUtc: new Date(referenceTimeMs + this.sessionLifetimeMs - this.warningWindowMs).toISOString()
    }, referenceTimeMs);
  }

  private applyTiming(timing: SessionLifecycleTiming, referenceTimeMs: number): boolean {
    const expiresAt = new Date(timing.expiresAtUtc);
    const warningAt = new Date(timing.warningAtUtc);
    if (!this.tryRememberLifecycleDurations(expiresAt, warningAt, referenceTimeMs)) {
      this.markBlocked(sessionUnavailableMessage);
      return false;
    }

    this.clearTimers();

    if (referenceTimeMs >= expiresAt.getTime()) {
      this.transitionToExpired(sessionExpiryMessage);
      return true;
    }

    if (referenceTimeMs >= warningAt.getTime()) {
      this.enterWarning(expiresAt);
      return true;
    }

    this.updateSnapshot({
      phase: 'active'
    });

    this.warningTimerId = globalThis.setTimeout(() => {
      this.enterWarning(expiresAt);
    }, warningAt.getTime() - referenceTimeMs);

    this.expiryTimerId = globalThis.setTimeout(() => {
      this.transitionToExpired(sessionExpiryMessage);
    }, expiresAt.getTime() - referenceTimeMs);

    return true;
  }

  private enterWarning(expiresAt: Date): void {
    this.clearCountdownTimer();
    this.updateWarningSnapshot(expiresAt);

    this.countdownTimerId = globalThis.setInterval(() => {
      if (Date.now() >= expiresAt.getTime()) {
        this.transitionToExpired(sessionExpiryMessage);
        return;
      }

      this.updateWarningSnapshot(expiresAt);
    }, 1000);
  }

  private updateWarningSnapshot(expiresAt: Date): void {
    this.updateSnapshot({
      phase: 'warning',
      title: sessionWarningTitle,
      message: sessionWarningMessage,
      secondsRemaining: Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000))
    });
  }

  private transitionToExpired(message: string): void {
    this.clearTimersAndRefreshQueue();
    this.updateSnapshot({
      phase: 'expired',
      title: 'Session expired',
      message
    });
    this.navigateToExpiredRoute();
  }

  private navigateToExpiredRoute(): void {
    this.navigation.navigateToExpiredSession();
  }

  private updateSnapshot(snapshot: SessionLifecycleSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener(this.snapshot));
  }

  private async refreshSessionFromBff(): Promise<void> {
    this.clearRefreshQueue();
    this.refreshInFlight = true;
    this.lastRefreshStartedAtMs = Date.now();

    try {
      const result = await this.extendSessionUseCase.execute();
      if (result.kind === 'available') {
        this.applyStatus(result.status.serverTimeUtc, {
          expiresAtUtc: result.status.expiresAtUtc,
          warningAtUtc: result.status.warningAtUtc
        });
        return;
      }

      if (result.kind === 'session-required') {
        this.markSessionRequired(result.message);
        return;
      }

      this.markBlocked(result.message ?? sessionUnavailableMessage);
    } catch {
      this.markBlocked(sessionUnavailableMessage);
    } finally {
      this.refreshInFlight = false;
      if (this.refreshQueued && this.snapshot.phase === 'active') {
        this.refreshQueued = false;
        this.scheduleActivityRefresh();
      }
    }
  }

  private scheduleActivityRefresh(): void {
    if (this.refreshInFlight) {
      this.refreshQueued = true;
      return;
    }

    const delay = Math.max(0, activityRefreshMinimumIntervalMs - (Date.now() - this.lastRefreshStartedAtMs));
    if (this.refreshTimerId !== undefined) {
      globalThis.clearTimeout(this.refreshTimerId);
    }

    this.refreshTimerId = globalThis.setTimeout(() => {
      void this.refreshSessionFromBff();
    }, delay);
  }

  private clearTimers(): void {
    if (this.warningTimerId !== undefined) {
      globalThis.clearTimeout(this.warningTimerId);
      this.warningTimerId = undefined;
    }

    if (this.expiryTimerId !== undefined) {
      globalThis.clearTimeout(this.expiryTimerId);
      this.expiryTimerId = undefined;
    }

    this.clearCountdownTimer();
  }

  private clearTimersAndRefreshQueue(): void {
    this.clearTimers();
    this.clearRefreshQueue();
  }

  private clearRefreshQueue(): void {
    if (this.refreshTimerId !== undefined) {
      globalThis.clearTimeout(this.refreshTimerId);
      this.refreshTimerId = undefined;
    }

    this.refreshQueued = false;
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimerId !== undefined) {
      globalThis.clearInterval(this.countdownTimerId);
      this.countdownTimerId = undefined;
    }
  }

  private tryRememberLifecycleDurations(expiresAt: Date, warningAt: Date, referenceTimeMs: number): boolean {
    const expiresAtMs = expiresAt.getTime();
    const warningAtMs = warningAt.getTime();
    if (Number.isNaN(expiresAtMs) || Number.isNaN(warningAtMs)) {
      return false;
    }

    const sessionLifetimeMs = expiresAtMs - referenceTimeMs;
    const warningWindowMs = expiresAtMs - warningAtMs;
    if (warningWindowMs <= 0 || warningAtMs > expiresAtMs) {
      return false;
    }

    this.sessionLifetimeMs = Math.max(0, sessionLifetimeMs);
    this.warningWindowMs = warningWindowMs;
    return true;
  }
}